import { memo } from "react";
import { Link, useLocation } from "react-router";

import { cn } from "@/lib/chillnn/cn";

import { LangCodeForFeatureIslandHeader, type LangCodeForFeatureIslandHeaderProps } from "./components";

export type FeatureIslandHeaderLangNavigatorProps = LangCodeForFeatureIslandHeaderProps["navigator"];

type Props = {
	title: string;
	titleRightChildren?: React.ReactNode;

	navigator?: LangCodeForFeatureIslandHeaderProps["navigator"];
	activeLangCode?: LangCodeForFeatureIslandHeaderProps["activeLangCode"];

	rightChildren?: React.ReactNode;

	className?: string;
};

const FeatureIslandHeaderInner = memo(({ title, titleRightChildren, navigator, activeLangCode, rightChildren }: Props) => {
	const canShowLangCode = !!navigator && navigator.i18n && !!activeLangCode;

	return (
		<>
			<div className="flex flex-col gap-y-2 tablet:min-h-11 laptop:flex-row laptop:flex-wrap laptop:items-center laptop:justify-between">
				<div className="flex items-center gap-4 laptop:gap-12">
					<div className="flex flex-wrap items-center gap-2">
						<h2 className="font-semibold tracking-[0.15rem] laptop:text-lg">{title}</h2>
						{titleRightChildren}
					</div>
					{canShowLangCode && (
						<LangCodeForFeatureIslandHeader navigator={navigator} activeLangCode={activeLangCode} />
					)}
				</div>

				{rightChildren && (
					<div className="hidden laptop:ml-auto laptop:block laptop:shrink-0">{rightChildren}</div>
				)}
			</div>

			{rightChildren && (
				<div className="fixed bottom-0 left-0 right-0 z-50 border-t border-zinc-200 bg-white px-4 py-3 laptop:hidden">
					<div className="flex justify-center">{rightChildren}</div>
				</div>
			)}
		</>
	);
});

export const FeatureIslandHeader = memo(({ className, ...props }: Props) => {
	return (
		<header
			className={cn(
				"sticky top-12 z-50 border-b border-b-zinc-100 bg-white px-4 py-2 laptop:top-0 laptop:px-12 laptop:py-4",
				className,
			)}
		>
			<FeatureIslandHeaderInner {...props} />
		</header>
	);
});

export type FeatureTabItemType = {
	tabTitle: string;
	tabTitleRightChildren?: React.ReactNode;
	path: string;
	targetBlank?: boolean;
	tablet?: boolean; // tablet 時の表示フラグ
};

type HeaderAndTabProps = {
	tabProps: {
		tabArray: FeatureTabItemType[];
	};
} & { headerProps: Props };

const FeatureIslandTab = memo(({ tabArray }: HeaderAndTabProps["tabProps"]) => {
	const { pathname } = useLocation();
	// 末尾に `/` がある場合は削除する
	const formattedPath = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

	const activeTab = tabArray.find((tab) => tab.path === formattedPath)?.tabTitle;

	return (
		<>
			{tabArray.length ? (
				<div className="flex gap-x-5 overflow-x-auto hidden-scroll-bar">
					{tabArray.map((tab) => {
						const baseClassName = cn(
							"flex shrink-0 items-center gap-x-2 px-2 py-2 text-[15px] tracking-wider transition-colors duration-300 ease-in-out laptop:text-base",
							!tab.tablet && "hidden tablet:flex",
						);

						return (
							<Link
								key={tab.tabTitle}
								className={cn(
									baseClassName,
									activeTab === tab.tabTitle
										? // アクティブな場合
											"rounded-t-lg border-b-2 border-b-brand bg-white font-medium"
										: // 非アクティブな場合
											"border-b-2 border-b-white text-zinc-400 hover:border-b-zinc-200 hover:text-black",
								)}
								to={tab.path}
								target={tab.targetBlank ? "_blank" : undefined}
								viewTransition
							>
								<h4>{tab.tabTitle}</h4>
								{tab.tabTitleRightChildren}
							</Link>
						);
					})}
				</div>
			) : null}
		</>
	);
});

export const FeatureIslandHeaderAndTab = ({ tabProps, headerProps }: HeaderAndTabProps) => {
	return (
		<header
			className={cn(
				"sticky top-12 z-20 border-b border-b-zinc-100 bg-white px-4 py-4 pb-0 laptop:top-0 laptop:px-12",
			)}
		>
			<div className="flex flex-col gap-y-2">
				<FeatureIslandHeaderInner {...headerProps} />
				<FeatureIslandTab {...tabProps} />
			</div>
		</header>
	);
};
